import React, { Fragment } from "react";
import { Menu, Transition } from "@headlessui/react";
import clsx from "clsx";
import { CheckIcon, ChevronDownIcon, EllipsisVerticalIcon } from "@heroicons/react/20/solid";

export type DropdownItemIconPosition = "leading" | "trailing";

export interface DropdownItem {
  id?: string;
  label: React.ReactNode;
  onSelect?: () => void;
  href?: string;
  icon?: React.ReactNode;
  iconPosition?: DropdownItemIconPosition;
  actionLabel?: React.ReactNode;
  selected?: boolean;
  disabled?: boolean;
}

export interface DropdownSection {
  id?: string;
  items: DropdownItem[];
}

interface DropdownProps {
  sections: DropdownSection[];
  label?: React.ReactNode;
  srLabel?: string;
  iconOnly?: boolean;
  triggerIcon?: React.ReactNode;
  align?: "start" | "end";
  className?: string;
  buttonClassName?: string;
  menuClassName?: string;
}

const Dropdown = ({
  sections,
  label = "Options",
  srLabel = "Open options",
  iconOnly = false,
  triggerIcon,
  align = "start",
  className,
  buttonClassName,
  menuClassName,
}: DropdownProps) => {
  const filteredSections = sections.filter((section) => section.items.length > 0);

  return (
    <Menu as="div" className={clsx("relative inline-block text-left", className)}>
      {({ open }) => (
        <>
          <Menu.Button
            className={clsx(
              iconOnly
                ? "inline-flex items-center justify-center rounded-[10px] bg-neutral-100 p-[6px] text-neutral-500 hover:bg-neutral-100"
                : "inline-flex items-center justify-center gap-[8px] rounded-[6px] border border-neutral-300 bg-white py-[9px] pl-[17px] pr-[13px] text-sm font-medium leading-5 text-neutral-700 shadow-sm hover:bg-neutral-0",
              iconOnly ? "" : open && "bg-neutral-0",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7B61FF]",
              iconOnly ? "focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-100" : "",
              !iconOnly ? "focus-visible:ring-offset-2 focus-visible:ring-offset-white" : "",
              buttonClassName,
            )}
          >
            <span className="sr-only">{srLabel}</span>
            {iconOnly ? (
              triggerIcon || <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
            ) : (
              <>
                <span>{label}</span>
                <ChevronDownIcon className="h-5 w-5 text-neutral-600" aria-hidden="true" />
              </>
            )}
          </Menu.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items
              className={clsx(
                "absolute z-20 mt-2 w-56 overflow-hidden rounded-[6px] bg-white shadow-[0px_0px_0px_1px_rgba(0,0,0,0.05),0px_10px_15px_-3px_rgba(0,0,0,0.1),0px_4px_6px_-2px_rgba(0,0,0,0.05)] focus:outline-none",
                align === "end" ? "right-0 origin-top-right" : "left-0 origin-top-left",
                menuClassName,
              )}
            >
              {filteredSections.map((section, sectionIdx) => (
                <div
                  key={section.id || `dropdown-section-${sectionIdx}`}
                  className={clsx("py-1", sectionIdx > 0 && "border-t border-neutral-100")}
                >
                  {section.items.map((item, itemIdx) => (
                    <Menu.Item
                      key={item.id || `dropdown-item-${sectionIdx}-${itemIdx}`}
                      disabled={item.disabled}
                    >
                      {({ active, close }) => {
                        const itemClasses = clsx(
                          "flex w-full items-center justify-between px-4 py-2 text-left text-sm font-medium leading-5",
                          active && !item.disabled ? "bg-neutral-100 text-primary-600" : "",
                          !active && !item.disabled ? "text-neutral-700" : "",
                          item.disabled && "cursor-not-allowed text-neutral-400",
                        );
                        const iconClasses = clsx(
                          "h-5 w-5 shrink-0",
                          active && !item.disabled ? "text-primary-600" : "text-neutral-400",
                          item.disabled && "text-neutral-300",
                        );
                        const actionLabelClasses = clsx(
                          "shrink-0 text-sm font-medium leading-5",
                          active && !item.disabled ? "text-primary-600" : "text-neutral-700",
                          item.disabled && "text-neutral-300",
                        );
                        const selectedCheckClasses = clsx(
                          "h-5 w-5 shrink-0",
                          item.disabled ? "text-neutral-300" : "text-primary-600",
                        );
                        const showSelectedCheck =
                          Boolean(item.selected) &&
                          !item.actionLabel &&
                          !(item.icon && item.iconPosition === "trailing");

                        const itemBody = (
                          <>
                            <span className="flex min-w-0 items-center gap-3">
                              {item.icon && item.iconPosition !== "trailing" && (
                                <span className={iconClasses}>{item.icon}</span>
                              )}
                              <span
                                className={clsx(
                                  "truncate",
                                  item.selected &&
                                    !item.disabled &&
                                    !active &&
                                    "font-semibold text-neutral-900",
                                )}
                              >
                                {item.label}
                              </span>
                              {item.icon && item.iconPosition === "trailing" && (
                                <span className={iconClasses}>{item.icon}</span>
                              )}
                            </span>
                            {item.actionLabel && (
                              <span className={actionLabelClasses}>{item.actionLabel}</span>
                            )}
                            {showSelectedCheck && <CheckIcon className={selectedCheckClasses} />}
                          </>
                        );

                        if (item.href && !item.disabled) {
                          return (
                            <a
                              href={item.href}
                              className={itemClasses}
                              onClick={() => {
                                item.onSelect?.();
                                close();
                              }}
                            >
                              {itemBody}
                            </a>
                          );
                        }

                        return (
                          <button
                            type="button"
                            className={itemClasses}
                            disabled={item.disabled}
                            onClick={() => {
                              if (item.disabled) {
                                return;
                              }
                              item.onSelect?.();
                              close();
                            }}
                          >
                            {itemBody}
                          </button>
                        );
                      }}
                    </Menu.Item>
                  ))}
                </div>
              ))}
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
};

export default Dropdown;
